import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  LmsTranscodingPreset,
  LmsTranscodingPresetDetail,
  CreateLmsTranscodingPresetRequest,
} from './live-presets-types';

// ============================================
// Query keys
// ============================================

export const livePresetKeys = {
  all: ['livestream', 'transcoding_presets'] as const,
  list: (serviceId: string) =>
    ['livestream', 'transcoding_presets', serviceId] as const,
  detail: (serviceId: string, presetId: string) =>
    ['livestream', 'transcoding_preset_detail', serviceId, { presetId }] as const,
};

// ============================================
// Queries
// ============================================

export function useLmsTranscodingPresets(serviceId: string) {
  return useQuery<LmsTranscodingPreset[]>({
    queryKey: livePresetKeys.list(serviceId),
    enabled: !!serviceId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/presets`);
      return res.data?.data ?? res.data ?? [];
    },
  });
}

export function useLmsTranscodingPresetDetail(
  serviceId: string,
  presetId: string | null,
) {
  return useQuery<LmsTranscodingPresetDetail>({
    queryKey: livePresetKeys.detail(serviceId, presetId ?? ''),
    enabled: !!serviceId && !!presetId,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/presets/${presetId}`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateLmsTranscodingPreset(
  serviceId: string,
  options?: UseMutationOptions<LmsTranscodingPreset[], Error, CreateLmsTranscodingPresetRequest[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLmsTranscodingPresetRequest[]) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, 'streams')}/presets`, payload);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: livePresetKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Submit a batch of preset profiles (POST to the presets endpoint).
 * Used by the Create Event Job preset-select flow when no preset exists yet.
 */
export function useSubmitLmsPresetProfiles(
  serviceId: string,
  options?: UseMutationOptions<void, Error, CreateLmsTranscodingPresetRequest[]>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLmsTranscodingPresetRequest[]) => {
      await serviceApiV3.post(`${mediaBase(serviceId, 'streams')}/presets`, payload);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: livePresetKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteLmsTranscodingPreset(
  serviceId: string,
  options?: UseMutationOptions<void, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (presetId: string) => {
      await serviceApiV3.delete(`${mediaBase(serviceId, 'streams')}/presets/${presetId}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: livePresetKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
